import { pb } from './pocketbase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const generateDocuments = async (folderId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/contracts/generate-documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
      },
      body: JSON.stringify({ folderId }),
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de la génération des documents');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la génération des documents:', error);
    throw error;
  }
};

export const generateRupture = async (ruptureData) => {
  try {    
    const response = await fetch(`${API_BASE_URL}/api/contracts/generate-rupture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
      },
      body: JSON.stringify({ ruptureData }),
    });
    
    if (!response.ok) {
      throw new Error('Erreur lors de la génération de la rupture de contrat');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la génération de la rupture de contrat:', error);
    throw error;
  }
};

export const generateCertificat = async (folderId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/contracts/generate-certificat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
      },
      body: JSON.stringify({ folderId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Erreur serveur inconnue' }));
      throw new Error(errorData.details || errorData.error || 'Erreur lors de la génération du certificat');
    }
    
    const contentDisposition = response.headers.get('content-disposition');
    let fileName = 'certificat-reussite.pdf'; // Default filename
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, fileName };

  } catch (error) {
    console.error('Erreur lors de la génération du certificat:', error);
    throw error;
  }
};

export const updateCertificat = async (contractData) => {
  try {
    console.log('Données envoyées à update-certificat:', contractData);
    
    const response = await fetch(`${API_BASE_URL}/api/contracts/update-certificat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
      },
      body: JSON.stringify(contractData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Erreur serveur inconnue' }));
      throw new Error(errorData.details || errorData.error || 'Erreur lors de la mise à jour du contrat et des documents');
    }
    
    // La route renvoie maintenant un objet JSON avec des informations sur les documents générés
    const responseData = await response.json();
    console.log('Réponse de update-certificat:', responseData);
    
    return responseData;

  } catch (error) {
    console.error('Erreur lors de la mise à jour du contrat et des documents:', error);
    throw error;
  }
};

//getById
export const getContractById = async (id) => {
  try {   
    if (!id) {
      console.error("ID de contrat non fourni");
      return null;
    }
    
    // Utiliser l'API PocketBase pour récupérer le contrat avec les relations expandées
    const record = await pb.collection('contracts').getOne(id, {
      expand: 'student_id,company_id,center_id,tutor_id,second_tutor_id'
    });
    
    return record;
  } catch (error) {
    if (error.status === 404) {
      console.error(`Contrat avec ID ${id} non trouvé`);
      return null;
    }
    
    return null;
  }
};

//update
export const updateContract = async (id, contractData, regenerateDocuments = false) => {
  try {
    if (regenerateDocuments) {
      // Si on veut régénérer les documents, on utilise updateCertificat
      return await updateCertificat({
        ...contractData,
        contractId: id
      });
    } else {
      // Extract tutor data if present
      const { tutor, second_tutor, ...contractDataWithoutTutors } = contractData;
      
      // Update the contract first
      const response = await pb.collection('contracts').update(id, contractDataWithoutTutors);
      
      // If there's tutor data, update or create the tutor
      if (tutor) {
        try {
          // Get the current contract to check if it already has a tutor_id
          const currentContract = await pb.collection('contracts').getOne(id);
          
          if (currentContract.tutor_id) {
            // Update existing tutor
            await pb.collection('tutors').update(currentContract.tutor_id, tutor);
          } else {
            // Create new tutor and link it to the contract
            const tutorWithIds = {
              ...tutor,
              company_id: contractData.company_id || currentContract.company_id,
              contract_id: id,
              partner_id: pb.authStore.record.partner_id
            };
            
            const createdTutor = await pb.collection('tutors').create(tutorWithIds);
            await pb.collection('contracts').update(id, { tutor_id: createdTutor.id });
          }
        } catch (tutorError) {
          console.error('Erreur lors de la mise à jour du tuteur principal:', tutorError);
        }
      }
      
      // If there's second tutor data, update or create the second tutor
      if (second_tutor) {
        try {
          // Get the current contract to check if it already has a second_tutor_id
          const currentContract = await pb.collection('contracts').getOne(id);
          
          if (currentContract.second_tutor_id) {
            // Update existing second tutor
            await pb.collection('tutors').update(currentContract.second_tutor_id, second_tutor);
          } else {
            // Create new second tutor and link it to the contract
            const secondTutorWithIds = {
              ...second_tutor,
              company_id: contractData.company_id || currentContract.company_id,
              contract_id: id,
              is_second_tutor: true,
              partner_id: pb.authStore.record.partner_id
            };
            
            const createdSecondTutor = await pb.collection('tutors').create(secondTutorWithIds);
            await pb.collection('contracts').update(id, { second_tutor_id: createdSecondTutor.id });
          }
        } catch (secondTutorError) {
          console.error('Erreur lors de la mise à jour du second tuteur:', secondTutorError);
        }
      }
      
      return response;
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour du contrat:', error);
    throw error;
  }
};

// delete
export const deleteContract = async (id) => {
  try {
    await pb.collection('contracts').delete(id);
    return true;
  } catch (error) {
    console.error('Error deleting contract:', error);
    throw error;
  }
};

// Récupérer les contrats d'un étudiant spécifique
export const getStudentContracts = async (studentId) => {
  try {
    const records = await pb.collection('contracts').getList(1, 50, {
      filter: `student_id = "${studentId}"`,
      sort: '-created',
      expand: 'student_id,company_id,center_id'
    });
    return records;
  } catch (error) {
    console.error('Error fetching student contracts:', error);
    throw error;
  }
};

// Récupérer les conventions d'un étudiant spécifique
export const getStudentConventions = async (studentId) => {
  try {
    const records = await pb.collection('contracts').getList(1, 50, {
      filter: `student_id = "${studentId}" && convention != ""`,
      sort: '-created',
      expand: 'student_id,company_id,center_id'
    });
    return records;
  } catch (error) {
    console.error('Error fetching student conventions:', error);
    throw error;
  }
};

// Récupérer à la fois les contrats et les conventions d'un étudiant
export const getStudentContractsAndConventions = async (studentId) => {
  try {
    // Récupérer tous les contrats de l'étudiant (avec ou sans convention)
    const records = await pb.collection('contracts').getList(1, 100, {
      filter: `student_id = "${studentId}"`,
      sort: '-created',
      expand: 'student_id,company_id,center_id'
    });
    
    // On marque chaque document avec un type pour l'affichage
    const items = records.items.map(item => {
      // Si le document a à la fois un contrat et une convention
      if (item.document && item.convention) {
        return [
          { ...item, documentType: 'document', fileField: 'document' },
          { ...item, documentType: 'convention', fileField: 'convention' }
        ];
      } 
      // Si le document a seulement un contrat
      else if (item.document) {
        return [{ ...item, documentType: 'document', fileField: 'document' }];
      }
      // Si le document a seulement une convention
      else if (item.convention) {
        return [{ ...item, documentType: 'convention', fileField: 'convention' }];
      }
      // Cas où il n'y a ni document ni convention (ne devrait pas arriver)
      return [];
    });
    
    // Aplatir le tableau de tableaux
    const flatItems = items.flat();
    
    return {
      items: flatItems,
      totalItems: flatItems.length,
      totalPages: 1
    };
  } catch (error) {
    console.error('Error fetching student contracts and conventions:', error);
    throw error;
  }
};

// Récupérer les contrats d'une entreprise spécifique
export const getCompanyContracts = async (companyId) => {
  try {
    const records = await pb.collection('contracts').getList(1, 50, {
      filter: `company_id = "${companyId}"`,
      sort: '-created',
      expand: 'student_id,company_id,center_id'
    });
    return records;
  } catch (error) {
    console.error('Error fetching company contracts:', error);
    throw error;
  }
};

// Compter les contrats actifs d'une entreprise
export const countActiveCompanyContracts = async (companyId) => {
  try {
    const result = await pb.collection('contracts').getList(1, 1, {
      filter: `company_id = "${companyId}" && actif = true`,
      // On utilise une petite pagination car on a juste besoin du total
      sort: '-created'
    });
    
    return result.totalItems;
  } catch (error) {
    console.error('Error counting active company contracts:', error);
    return 0;
  }
};


// Télécharger un document (contrat ou convention)
export const downloadContract = async (contract) => {
  // Déterminer quel champ de fichier utiliser (document ou convention)
  const fileField = contract.fileField || 'document';
  const fileName = contract[fileField];
  
  if (!contract || !contract.id || !fileName) {
    console.error(`Impossible de télécharger ce ${fileField} : document invalide ou fichier manquant`);
    return false;
  }
  
  try {
    // Construire l'URL du fichier pour le téléchargement direct depuis PocketBase
    const fileUrl = `${pb.baseUrl}/api/files/contracts/${contract.id}/${fileName}`;
    
    // Télécharger le fichier avec fetch
    const response = await fetch(fileUrl);
    
    if (!response.ok) {
      throw new Error(`Erreur lors du téléchargement: ${response.statusText}`);
    }
    
    // Nom du fichier à utiliser pour le téléchargement
    const downloadFilename = fileName || `${fileField}-${contract.id}.pdf`;
    
    // Convertir la réponse en blob
    const blob = await response.blob();
    
    // Créer une URL objet pour le blob
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Créer un lien invisible et simuler un clic pour télécharger
    const link = window.document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', downloadFilename);
    link.style.display = 'none';
    window.document.body.appendChild(link);
    link.click();
    
    // Nettoyer
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl); // Libérer la mémoire
    
    return true;
  } catch (error) {
    console.error(`Erreur lors du téléchargement du ${fileField}:`, error);
    return false;
  }
};


export const createContract = async (contractData) => {
  try {
    if (pb.authStore.isValid && pb.authStore.model) {
      // Extract tutor data from contract data if present
      const { tutor, second_tutor, ...contractDataWithoutTutors } = contractData;
      
      const contractWithPartnerId = {
        ...contractDataWithoutTutors,
        partner_id: pb.authStore.record.partner_id
      };
      
      // Create the contract first
      const createdContract = await pb.collection('contracts').create(contractWithPartnerId);
      
      // If contract creation was successful and there's tutor data, create the tutor(s)
      if (createdContract && tutor) {
        try {
          // Add company_id and contract_id to tutor data
          const tutorWithIds = {
            ...tutor,
            company_id: contractData.company_id,
            contract_id: createdContract.id,
            partner_id: pb.authStore.record.partner_id
          };
          
          // Create the tutor
          const createdTutor = await pb.collection('tutors').create(tutorWithIds);
          
          // Update the contract with the tutor_id
          await pb.collection('contracts').update(createdContract.id, {
            tutor_id: createdTutor.id
          });
        } catch (tutorError) {
          console.error('Erreur lors de la création du tuteur principal:', tutorError);
          // Continue even if tutor creation fails
        }
      }
      
      // If there's a second tutor, create it as well
      if (createdContract && second_tutor) {
        try {
          // Add company_id and contract_id to second tutor data
          const secondTutorWithIds = {
            ...second_tutor,
            company_id: contractData.company_id,
            contract_id: createdContract.id,
            is_second_tutor: true,
            partner_id: pb.authStore.record.partner_id
          };
          
          // Create the second tutor
          const createdSecondTutor = await pb.collection('tutors').create(secondTutorWithIds);
          
          // Update the contract with the second_tutor_id
          await pb.collection('contracts').update(createdContract.id, {
            second_tutor_id: createdSecondTutor.id
          });
        } catch (secondTutorError) {
          console.error('Erreur lors de la création du second tuteur:', secondTutorError);
          // Continue even if second tutor creation fails
        }
      }
      
      return createdContract;
    } else {
      console.error('Aucun utilisateur authentifié. Impossible de créer le contrat sans partner_id.');
      return null;
    }
  } catch (error) {
    console.error('Erreur lors de la création du contrat:', error);
    throw error;
  }
};

// Service unifié pour récupérer toutes les données de comptabilité
export const getComptabilityDataByFolderId = async (folderId) => {
  try {
    if (!folderId) {
      console.error("ID de dossier non fourni");
      return {
        contractId: null,
        echeances: [],
        detailsFacturation: {},
        engagementsFraisAnnexe: []
      };
    }
    
    // Récupérer d'abord le folder pour obtenir le contract_id
    const folder = await pb.collection('folders').getOne(folderId, {
      fields: 'id,contract_id'
    });

    if (!folder.contract_id) {
      console.log(`Aucun contract_id trouvé pour le dossier ${folderId}`);
      return {
        contractId: null,
        echeances: [],
        detailsFacturation: {},
        engagementsFraisAnnexe: [],
        contractInfo: {
          numero_interne: null,
          numero_deca: null,
          numero_externe: null,
          opco_status: null
        }
      };
    }

    // Récupérer le contrat avec tous les champs nécessaires
    const contract = await pb.collection('contracts').getOne(folder.contract_id, {
      fields: 'id,echeances,detailsFacturation,engagementsFraisAnnexe,numero_interne,numero_deca,numero_externe,opco_status'
    });
    
    console.log('Contrat récupéré:', contract);
    
    // Parser les échéances
    let echeancesData = [];
    if (contract.echeances) {
      try {
        echeancesData = typeof contract.echeances === 'string' 
          ? JSON.parse(contract.echeances) 
          : contract.echeances;
      } catch (parseError) {
        console.error('Erreur lors du parsing des échéances:', parseError);
      }
    }
    
    // Parser les détails de facturation
    let detailsFacturationData = {};
    if (contract.detailsFacturation) {
      try {
        detailsFacturationData = typeof contract.detailsFacturation === 'string' 
          ? JSON.parse(contract.detailsFacturation) 
          : contract.detailsFacturation;
      } catch (parseError) {
        console.error('Erreur lors du parsing des détails de facturation:', parseError);
      }
    }
    
    // Parser les engagements frais annexe
    let engagementsFraisAnnexeData = [];
    if (contract.engagementsFraisAnnexe) {
      try {
        engagementsFraisAnnexeData = typeof contract.engagementsFraisAnnexe === 'string' 
          ? JSON.parse(contract.engagementsFraisAnnexe) 
          : contract.engagementsFraisAnnexe;
      } catch (parseError) {
        console.error('Erreur lors du parsing des engagements frais annexe:', parseError);
      }
    }
    
    const result = {
      contractId: contract.id,
      echeances: Array.isArray(echeancesData) ? echeancesData : [],
      detailsFacturation: detailsFacturationData || {},
      engagementsFraisAnnexe: Array.isArray(engagementsFraisAnnexeData) ? engagementsFraisAnnexeData : [],
      contractInfo: {
        numero_interne: contract.numero_interne || null,
        numero_deca: contract.numero_deca || null,
        numero_externe: contract.numero_externe || null,
        opco_status: contract.opco_status || null
      }
    };
    
    console.log('Données de comptabilité récupérées:', result);
    return result;
  } catch (error) {
    console.error('Erreur lors de la récupération des données de comptabilité:', error);
    return {
      contractId: null,
      echeances: [],
      detailsFacturation: {},
      engagementsFraisAnnexe: [],
      contractInfo: {
        numero_interne: null,
        numero_deca: null,
        numero_externe: null,
        opco_status: null
      }
    };
  }
};

// Supprimer une rupture de contrat
export const deleteRupture = async (contractId) => {
  try {
    if (!contractId) {
      throw new Error("ID de contrat non fourni");
    }

    // Mettre à jour le contrat pour supprimer les champs liés à la rupture
    const updateData = {
      rupture: null,
      rupture_signature_status: "",
      procedureRupture: null,
      training_continued_after_break: null,
      rupture_reason: "",
      rupture_date: null,
      reason_for_break: "",
      date_for_break: null
    };

    const updatedContract = await pb.collection('contracts').update(contractId, updateData);
    return updatedContract;
  } catch (error) {
    console.error('Erreur lors de la suppression de la rupture:', error);
    throw error;
  }
};

// Récupérer les frais annexes d'un contrat par folderId
export const getContractFraisAnnexesByFolderId = async (folderId) => {
  try {
    if (!folderId) {
      console.error("ID de dossier non fourni");
      return {};
    }
    
    // Récupérer d'abord le folder pour obtenir le contract_id
    const folder = await pb.collection('folders').getOne(folderId, {
      fields: 'id,contract_id'
    });

    if (!folder.contract_id) {
      console.log(`Aucun contract_id trouvé pour le dossier ${folderId}`);
      return {};
    }

    // Récupérer le contrat avec le contract_id du folder
    const contract = await pb.collection('contracts').getOne(folder.contract_id, {
      fields: 'id,engagementsFraisAnnexe'
    });
    
    // Parser les frais annexes depuis le JSON stocké
    let fraisAnnexesData = [];
    if (contract.engagementsFraisAnnexe) {
      try {
        fraisAnnexesData = typeof contract.engagementsFraisAnnexe === 'string' 
          ? JSON.parse(contract.engagementsFraisAnnexe) 
          : contract.engagementsFraisAnnexe;
      } catch (parseError) {
        console.error('Erreur lors du parsing des frais annexes:', parseError);
        return {};
      }
    }
    
    // Transformer le tableau en objet avec les montants par type de frais
    const fraisMap = {};
    if (Array.isArray(fraisAnnexesData)) {
      fraisAnnexesData.forEach(frais => {
        const natureFrais = frais.natureFrais?.toLowerCase();
        if (natureFrais && frais.montantTotal !== undefined) {
          fraisMap[natureFrais] = frais.montantTotal;
        }
      });
    }
    
    return fraisMap;
  } catch (error) {
    console.error('Erreur lors de la récupération des frais annexes:', error);
    return {};
  }
};
